// Attributes management view for the Layout Config screen.
//
// Replaces the main inbox area when the user clicks the "Attributes"
// header button. Mirrors the reference's FieldIdentifier index
// (ocr-ui/src/modules/document-extraction/config-studio/field-identifier
// /index.jsx) — tabs for LINE ITEM / FIELD ITEM, list rows with
// edit/delete actions, "Add Attributes" CTA that opens AttributeFormModal.
//
// Data sources:
//   POST /document_extraction/lineitem_attribute_list  (line_item tab)
//   POST /document_extraction/fielditem_attribute_list (field_item tab)
//
// Mutations:
//   /lineitem_attribute_insert | /fielditem_attribute_insert
//   /lineitem_attribute_update | /fielditem_attribute_update
//   /config_attribute_delete  (shared, scoped by field_type)

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Layers,
  Copy,
  Inbox,
  Search,
  RefreshCw,
  Plus,
  Pencil,
  Trash2,
  Loader2,
} from "lucide-react";

import { ocrApi } from "../../../services/ocrApi";
import type {
  AttributeRecord,
  AttributeSection,
} from "../../../types/documents";
import { AttributeFormModal, type AttributeFormMode } from "./AttributeFormModal";

interface AttributesViewProps {
  /** Fires when the user clicks "Back to Template Engine" to dismiss the view. */
  onBack: () => void;
}

const TABS: { id: AttributeSection; label: string; Icon: typeof Layers }[] = [
  { id: "line_item", label: "LINE ITEM", Icon: Layers },
  { id: "field_item", label: "FIELD ITEM", Icon: Copy },
];

export function AttributesView({ onBack }: AttributesViewProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<AttributeSection>("line_item");
  const [search, setSearch] = useState("");

  // Modal state — open/mode/data.
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<AttributeFormMode>("add");
  const [modalData, setModalData] = useState<AttributeRecord | null>(null);

  const attributesQuery = useQuery({
    queryKey: ["ocr", "attributes", activeTab],
    queryFn: () => ocrApi.listAttributes(activeTab),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const rows = attributesQuery.data ?? [];

  const filtered = useMemo<AttributeRecord[]>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.identifier, r.type, r.pattern, r.description, r.status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const openAdd = () => {
    setModalMode("add");
    setModalData(null);
    setModalOpen(true);
  };

  const openEdit = (row: AttributeRecord) => {
    setModalMode("edit");
    setModalData(row);
    setModalOpen(true);
  };

  // Invalidate every cache that reads attribute identifiers for the
  // active section — both the Attributes table itself and the
  // verification cockpit's per-section "suggestions" query
  // (["ocr","verification","suggestions",<section>]). Without the second
  // invalidation, the cockpit's draw dropdown shows the previous list
  // until the user manually refreshes (5-min `staleTime`).
  const invalidateAttributeReaders = () => {
    queryClient.invalidateQueries({
      queryKey: ["ocr", "attributes", activeTab],
    });
    queryClient.invalidateQueries({
      queryKey: ["ocr", "verification", "suggestions", activeTab],
    });
  };

  const deleteMutation = useMutation({
    mutationFn: (row: AttributeRecord) =>
      ocrApi.deleteAttribute(activeTab, String(row.id)),
    onSuccess: (data) => {
      const ok =
        data?.status === "success" || data?.message === "success";
      if (ok) {
        toast.success("Attribute deleted successfully!");
        invalidateAttributeReaders();
      } else {
        toast.error(
          data?.message || data?.status || "Failed to delete attribute.",
        );
      }
    },
    onError: (err: unknown) => {
      toast.error(
        err instanceof Error ? err.message : "An error occurred. Please try again.",
      );
    },
  });

  const handleDelete = (row: AttributeRecord) => {
    if (
      !window.confirm(
        `Delete attribute "${row.identifier}"? This can't be undone.`,
      )
    ) {
      return;
    }
    deleteMutation.mutate(row);
  };

  const handleSaved = () => {
    invalidateAttributeReaders();
  };

  return (
    <div className="flex h-full w-full flex-col overflow-auto bg-gray-50">
      {/* === Page header ============================================== */}
      <header className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-8 py-5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-3 bg-blue-50 rounded-xl flex-shrink-0">
            <Layers size={22} className="text-blue-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900">Attributes</h1>
            <p className="text-sm text-gray-500">
              Manage your field and line item attributes.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          <ArrowLeft size={16} className="text-gray-500" />
          Back to Template Engine
        </button>
      </header>

      {/* === Card with tabs + table ================================== */}
      <div className="flex-1 px-8 py-6">
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Tabs */}
          <div className="border-b border-gray-200 px-6">
            <div className="flex items-center gap-6">
              {TABS.map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setActiveTab(tab.id);
                      setSearch("");
                    }}
                    className={`relative inline-flex items-center gap-2 py-4 text-sm font-semibold tracking-wider transition ${
                      active
                        ? "text-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <tab.Icon
                      size={16}
                      className={active ? "text-blue-600" : "text-gray-400"}
                    />
                    {tab.label}
                    {active ? (
                      <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-blue-600" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sub-header: title + count, search, refresh, add */}
          <div className="flex flex-wrap items-center gap-3 px-6 py-4">
            <div className="flex items-center gap-2">
              <Inbox className="h-5 w-5 text-blue-500" />
              <h2 className="text-base font-semibold text-gray-900">
                Attributes
              </h2>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 tabular-nums">
                {attributesQuery.isLoading ? "…" : filtered.length}
              </span>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search field identifier"
                  className="w-72 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <button
                type="button"
                onClick={() => attributesQuery.refetch()}
                disabled={attributesQuery.isFetching}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-4 w-4 ${
                    attributesQuery.isFetching ? "animate-spin" : ""
                  }`}
                />
                Refresh
              </button>
              <button
                type="button"
                onClick={openAdd}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                Add Attributes
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-sm">
              <thead className="border-y border-gray-100 bg-gray-50/70 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-6 py-3">Field Identifier</th>
                  <th className="px-6 py-3">Data Type</th>
                  <th className="px-6 py-3">Regex Pattern</th>
                  <th className="px-6 py-3">Regex Description</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {attributesQuery.isLoading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-16 text-center text-sm text-gray-500"
                    >
                      Loading attributes…
                    </td>
                  </tr>
                ) : attributesQuery.isError ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center">
                      <div className="mx-auto max-w-md rounded-md border border-rose-200 bg-rose-50 p-4 text-left text-rose-700">
                        <p className="font-medium">Couldn't load attributes.</p>
                        <p className="mt-1 text-rose-600">
                          {attributesQuery.error instanceof Error
                            ? attributesQuery.error.message
                            : "Unknown error"}
                        </p>
                        <button
                          type="button"
                          onClick={() => attributesQuery.refetch()}
                          className="mt-3 inline-flex items-center rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100"
                        >
                          Retry
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-20">
                      <div className="flex flex-col items-center text-center">
                        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                          <Inbox className="h-7 w-7" />
                        </div>
                        <p className="text-base font-semibold text-gray-900">
                          No attributes found
                        </p>
                        <p className="mt-1 max-w-sm text-sm text-gray-500">
                          {search
                            ? "Try a different search term."
                            : "Start by adding your first attribute to manage extraction rules."}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 font-medium text-gray-900">
                        {row.identifier}
                      </td>
                      <td className="px-6 py-3 text-gray-600">
                        {row.type || "—"}
                      </td>
                      <td className="px-6 py-3 font-mono text-xs text-gray-600">
                        {row.pattern || "—"}
                      </td>
                      <td className="px-6 py-3 text-gray-600">
                        {row.description || "—"}
                      </td>
                      <td className="px-6 py-3">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-6 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            className="rounded-md p-1.5 text-gray-400 hover:bg-indigo-50 hover:text-indigo-600"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row)}
                            disabled={
                              deleteMutation.isPending &&
                              deleteMutation.variables?.id === row.id
                            }
                            className="rounded-md p-1.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                            title="Delete"
                          >
                            {deleteMutation.isPending &&
                            deleteMutation.variables?.id === row.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Add / Edit modal */}
      <AttributeFormModal
        open={modalOpen}
        mode={modalMode}
        section={activeTab}
        initial={modalData}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}

// -------------------------------------------------------------- helpers

function StatusBadge({ status }: { status: string }) {
  if (!status) return <span className="text-gray-400">—</span>;
  const isActive = status.toLowerCase() === "active";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${
        isActive
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
          : "bg-gray-100 text-gray-600 ring-gray-200"
      }`}
    >
      {status}
    </span>
  );
}
